import { useState, useCallback } from 'react';

const LOCATION_BANNER_DISMISSED = 'location_banner_dismissed';

export const useLocationPermission = () => {
  const [permission, setPermission] = useState<PermissionState>('prompt');
  const [error, setError] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    // Check localStorage on init
    return localStorage.getItem(LOCATION_BANNER_DISMISSED) === 'true';
  });

  const checkPermission = useCallback(async (): Promise<boolean> => {
    if (!navigator.permissions) {
      return 'geolocation' in navigator;
    }

    try {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      setPermission(result.state);
      
      result.onchange = () => {
        setPermission(result.state);
      };
      
      return result.state === 'granted';
    } catch {
      return 'geolocation' in navigator;
    }
  }, []);

  const dismissBanner = useCallback(() => {
    setBannerDismissed(true);
    localStorage.setItem(LOCATION_BANNER_DISMISSED, 'true');
  }, []);

  const resetBanner = useCallback(() => {
    setBannerDismissed(false);
    localStorage.removeItem(LOCATION_BANNER_DISMISSED);
  }, []);
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported by your browser');
      return false;
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        () => {
          setPermission('granted');
          setError(null);
          resolve(true);
        },
        (err) => {
          switch(err.code) {
            case 1: 
              setPermission('denied');
              setError('Location permission denied. Please enable it in your browser settings.');
              break;
            case 2:
              setError('Location unavailable. Please check your device settings.');
              break;
            case 3:
              setError('Location request timed out.');
              break;
            default:
              setError('Failed to get location.');
          }
          resolve(false);
        },
        { maximumAge: 0, timeout: 1000, enableHighAccuracy: false }
      );
    });
  }, []);

  const openSettingsGuide = () => {
    const guides = {
      chrome: 'chrome://settings/content/location',
      firefox: 'about:preferences#privacy',
      safari: 'x-apple.systempreferences:com.apple.preference.security?Privacy_LocationServices'
    };
    
    // You can show a modal with instructions instead
    alert(`To enable location:
1. Click the lock/padlock icon in your address bar
2. Find "Location" or "Site settings"
3. Change from "Block" to "Allow"
4. Refresh the page`);
  };

  return {
    permission,
    error,
    checkPermission,
    requestPermission,
    openSettingsGuide,
    bannerDismissed,
    dismissBanner,
    resetBanner,
    hasPermission: permission === 'granted',
    isDenied: permission === 'denied',
  };
};